import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Edit3, RotateCcw } from 'lucide-react'

import { ApiError, api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { DiffViewer } from '@/components/DiffViewer'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import type {
  AssistantApplyResponse,
  AssistantGenerateResponse,
  Prompt,
} from '@sdr-agend/admin-schemas'

interface AssistantResultStepProps {
  result: AssistantGenerateResponse
  campaignId: string
  /** Triggered by the "Refinar" button — goes back to step 1 preserving state. */
  onRefine: () => void
  /** Triggered by "Editar manualmente" — opens the NewPromptDialog with the
   * generated systemPrompt as the default value. */
  onEditManually: (systemPrompt: string) => void
  /** Triggered after a successful apply (creates new prompt OR new version). */
  onApplied: (prompt: AssistantApplyResponse) => void
}

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

/**
 * Story 3.18 §AC #7 — Step 4: Result.
 *
 * Renders the generated systemPrompt + rationale + the diff vs the campaign's
 * current active prompt (when applicable). Buttons: Refinar (back to step 1),
 * "Editar manualmente" (hands off to Story 3.15's NewPromptDialog), "Aplicar
 * como nova versão" (POST /admin/prompts/assistant/apply).
 */
export function AssistantResultStep({
  result,
  campaignId,
  onRefine,
  onEditManually,
  onApplied,
}: AssistantResultStepProps): React.ReactElement {
  const queryClient = useQueryClient()
  const [toast, setToast] = useState<ToastState>({
    open: false,
    variant: 'default',
    title: '',
  })

  // Fetch the campaign's currently active prompt if a campaign was selected,
  // so we can render a diff. We page through /admin/prompts and pick the row
  // whose campaignId matches — the existing master list is small.
  const { data: promptsData } = useQuery<{ prompts: Prompt[] }>({
    queryKey: ['admin-prompts'],
    queryFn: async () => api<{ prompts: Prompt[] }>('/admin/prompts'),
    enabled: campaignId !== '',
  })

  const currentPrompt: Prompt | undefined = (promptsData?.prompts ?? []).find(
    (p) => p.campaignId === campaignId,
  )

  const applyMutation = useMutation({
    mutationFn: async () =>
      api<AssistantApplyResponse>('/admin/prompts/assistant/apply', {
        method: 'POST',
        body: JSON.stringify({
          campaignId,
          systemPrompt: result.systemPrompt,
          rationale: 'Versão gerada via PromptAssistant',
        }),
      }),
    onSuccess: (res) => {
      setToast({
        open: true,
        variant: 'default',
        title: res.mode === 'new' ? 'Prompt criado' : 'Nova versão aplicada',
        description:
          res.mode === 'new'
            ? 'O assistente cadastrou o primeiro prompt da campanha.'
            : 'A nova versão foi ativada para a campanha.',
      })
      void queryClient.invalidateQueries({ queryKey: ['admin-prompts'] })
      onApplied(res)
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiError ? formatApiError(err) : err.message
      setToast({
        open: true,
        variant: 'destructive',
        title: 'Falha ao aplicar',
        description: msg,
      })
    },
  })

  const canApply = campaignId !== ''

  return (
    <div className="grid gap-4">
      <div className="grid gap-2 rounded-md border bg-muted/30 p-3">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Justificativa do modelo
        </span>
        <p className="text-sm">{result.rationale || '(sem justificativa fornecida)'}</p>
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span>Modelo: <strong>{result.modelUsed}</strong></span>
          <span>Latência: <strong>{result.latencyMs}ms</strong></span>
          <span>
            Tokens: <strong>{result.tokenUsage.in}</strong> in /{' '}
            <strong>{result.tokenUsage.out}</strong> out
          </span>
        </div>
      </div>

      {currentPrompt !== undefined ? (
        <div className="grid gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Diff vs versão atual (v{currentPrompt.version})
          </span>
          <DiffViewer
            fromBody={currentPrompt.systemPrompt}
            toBody={result.systemPrompt}
            fromLabel={`Versão atual (v${currentPrompt.version})`}
            toLabel="Versão gerada"
          />
        </div>
      ) : (
        <div className="grid gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Prompt gerado
          </span>
          <pre className="whitespace-pre-wrap break-words rounded-md border bg-muted/20 p-3 font-mono text-xs leading-relaxed">
            {result.systemPrompt}
          </pre>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="outline" onClick={onRefine}>
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          Refinar
        </Button>
        <Button type="button" variant="outline" onClick={() => onEditManually(result.systemPrompt)}>
          <Edit3 className="h-4 w-4" aria-hidden="true" />
          Editar manualmente
        </Button>
        <Button
          type="button"
          onClick={() => applyMutation.mutate()}
          disabled={!canApply || applyMutation.isPending}
        >
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          {applyMutation.isPending ? 'Aplicando…' : 'Aplicar como nova versão'}
        </Button>
      </div>

      {!canApply && (
        <p className="text-xs text-amber-600">
          Selecione uma campanha no passo 1 para poder aplicar diretamente.
        </p>
      )}

      <Toast
        open={toast.open}
        onOpenChange={(o) => setToast((s) => ({ ...s, open: o }))}
        variant={toast.variant}
      >
        <div className="grid gap-1">
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.description !== undefined && (
            <ToastDescription>{toast.description}</ToastDescription>
          )}
        </div>
      </Toast>
    </div>
  )
}

function formatApiError(err: ApiError): string {
  if (err.bodyJson !== null && typeof err.bodyJson === 'object' && 'error' in err.bodyJson) {
    const e = (err.bodyJson as { error: { code?: string; message?: string } }).error
    return e.message ?? e.code ?? err.message
  }
  return err.message
}
